import axios from 'axios';
import { CONFIG } from 'variables/config';
import { authConfig } from 'services/integrations';

// A seller's reusable form design (leadrs-back FormTheme, /form-theme): shared by any number of
// integrations and pages. Every style field is optional — what a theme leaves blank falls back to the
// integration's own style.

export const THEME_STYLE_FIELDS = [
    'borderColor', 'borderWidth', 'borderRadius', 'backgroundColor', 'textColor', 'labelColor',
    'buttonBackgroundColor', 'buttonTextColor', 'inputBackgroundColor', 'inputBorderColor',
    'inputTextColor', 'fontFamily', 'fontSize', 'ctaText',
] as const;
export type ThemeStyleField = typeof THEME_STYLE_FIELDS[number];
export type ThemeStyle = Partial<Record<ThemeStyleField, string>>;

export interface FormThemeUsage {
    templateId: string;
    name: string;
    pages: string[];
    templateWide: boolean;
}

export interface FormTheme extends ThemeStyle {
    _id: string;
    name: string;
    usedBy?: FormThemeUsage[];
}

export const themeStyleOf = (source: Partial<Record<ThemeStyleField, unknown>> | null | undefined): ThemeStyle => {
    const style: ThemeStyle = {};
    if (!source) return style;
    THEME_STYLE_FIELDS.forEach((field) => {
        const value = source[field];
        if (typeof value === 'string' && value) style[field] = value;
    });
    return style;
};

export const fetchThemes = async (): Promise<FormTheme[]> =>
    (await axios.get(`${CONFIG.apiUrl}/form-theme`, authConfig())).data;

export const createTheme = async (name: string, style: ThemeStyle): Promise<FormTheme> =>
    (await axios.post(`${CONFIG.apiUrl}/form-theme`, { name, style }, authConfig())).data;

export const updateTheme = async (themeId: string, changes: { name?: string; style?: ThemeStyle }): Promise<FormTheme> =>
    (await axios.put(`${CONFIG.apiUrl}/form-theme/${themeId}`, changes, authConfig())).data;

export const duplicateTheme = async (themeId: string, name?: string): Promise<FormTheme> =>
    (await axios.post(`${CONFIG.apiUrl}/form-theme/${themeId}/duplicate`, { name }, authConfig())).data;

export const deleteTheme = async (themeId: string): Promise<void> => {
    await axios.delete(`${CONFIG.apiUrl}/form-theme/${themeId}`, authConfig());
};

// Both return the updated template.
export const setTemplateTheme = async (templateId: string, themeId: string | null) =>
    (await axios.patch(`${CONFIG.apiUrl}/form-template/${templateId}/theme`, { themeId }, authConfig())).data;

export const setPageTheme = async (templateId: string, landingPage: string, themeId: string | null) =>
    (await axios.patch(`${CONFIG.apiUrl}/form-template/${templateId}/page-theme`, { landingPage, themeId }, authConfig())).data;

export const fetchTheme = async (themeId: string): Promise<FormTheme> =>
    (await axios.get(`${CONFIG.apiUrl}/form-theme/${themeId}`, authConfig())).data;

// "Appliquer à…": for each listed integration, the full desired state of this theme on it — see
// PUT /form-theme/:themeId/usage. Returns where the theme is used afterwards.
export interface ThemeUsageEntry {
    templateId: string;
    templateWide: boolean;
    pages: string[];
}
export const updateThemeUsage = async (themeId: string, templates: ThemeUsageEntry[]): Promise<FormThemeUsage[]> =>
    (await axios.put(`${CONFIG.apiUrl}/form-theme/${themeId}/usage`, { templates }, authConfig())).data.usedBy;
